import type { ChatItem } from '../../../contracts/harness.types';
import { isTurnResultItem } from '../../../data/projects/lib/turnLifecycle';
import type { NestedAnswerTextSource } from './nestedAnswerText';

/**
 * Whether the latest answer must stay out of view, and a way to stop holding
 * it when the user stops the turn. Build once per chat: the hook subscribes by
 * identity.
 */
export interface AnswerRevealGate {
  isHeld: () => boolean;
  subscribe: (onChange: () => void) => () => void;
  release: () => void;
}

export const OPEN_ANSWER_REVEAL_GATE: AnswerRevealGate = {
  isHeld: () => false,
  subscribe: () => () => undefined,
  release: () => undefined,
};

/**
 * Drops the latest turn from its first result: answer words, a result card, a
 * plan, a question back, or words inside an Evidence step. The steps before it
 * stay. `latchedAnswerItemId` keeps the cut on an Evidence step whose words
 * left with its card: hiding the card clears the transcript it published.
 */
export const withholdLatestAnswer = (
  items: ChatItem[],
  nestedAnswerText: NestedAnswerTextSource,
  latchedAnswerItemId?: string
): ChatItem[] => {
  let latestUserIndex = -1;
  for (let index = items.length - 1; index >= 0; index -= 1) {
    if (items[index]?.kind === 'user_message') {
      latestUserIndex = index;
      break;
    }
  }
  if (latestUserIndex < 0) return items;
  for (let index = latestUserIndex + 1; index < items.length; index += 1) {
    const item = items[index];
    if (!item) continue;
    const isResult =
      item.id === latchedAnswerItemId ||
      isTurnResultItem(item) ||
      (item.kind === 'tool_use' && nestedAnswerText.read(items.slice(0, index + 1)));
    if (isResult) return items.slice(0, index);
  }
  return items;
};
